import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { IDownloadedFile, toDownloadedFile } from 'src/app/shared/utils/downloaded-file.util';
import { FileSizePipe } from 'src/app/config/filesize-pipe';

import { FilePreviewService } from './file-preview.service';
import type { IPreviewEntry, IPreviewMetadata } from './file-preview-dialog.component';

/** One document of a message, as `…/attachments/<id>/bundle` returns it. */
interface IBundleDocument {
  name: string;
  mime: string;
  size: number;
  /** Null when the bytes never travelled — the entry is listed, not openable. */
  attachment_id: number | null;
  /** `too_large` / `no_content`, or null. */
  skipped: string | null;
}

interface IPreviewBundle {
  attachment_id: number;
  filename: string;
  /** The message's own body, or null for a document that travelled in none. */
  body_attachment_id: number | null;
  headers: IPreviewMetadata;
  documents: IBundleDocument[];
}

/**
 * Opens the document a price was read from, given the document's id.
 *
 * Sits between the surfaces that only hold an id — the candidate tooltip and
 * its panel, the price journal, the observations table — and the route that
 * turns that id into bytes. Without it each of them builds the same one-line
 * fetcher.
 *
 * The content type is deliberately not trusted to describe the file: every
 * attachment route in the estate serves scriptable types as an opaque download
 * so they cannot execute inline. The file name is what survives that, and the
 * preview dialog renders from it.
 *
 * One call answers both questions the previewer asks — who wrote the message,
 * and what else it carried — because they come from the same row. Split in two,
 * a body opens with no sign of the tariff attached to it and a tariff with no
 * sign of the mail that sent it.
 */
@Injectable({ providedIn: 'root' })
export class PriceSourcePreviewService {
  private readonly attachmentsUrl = `${environment.BASE_URL_API}ai/kb/pricing-observations/attachments`;
  private readonly fileSize = new FileSizePipe();

  constructor(
    private http: HttpClient,
    private filePreview: FilePreviewService,
  ) {}

  open({ documentId, label }: { documentId: number; label: string }): void {
    if (!documentId) return;
    // Shared between the header block and the rail: the dialog asks for both,
    // and they are two readings of one response. `shareReplay` and not a second
    // GET; the refCount keeps a dialog closed before the answer arrives from
    // leaving the subscription alive.
    const bundle$ = this.bundle(documentId).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.filePreview.open({
      filename: label,
      selectedKey: String(documentId),
      source: () => this.downloadSourceFile(documentId),
      metadata: () => bundle$.pipe(map((bundle) => bundle?.headers ?? null)),
      entries: () => bundle$.pipe(map((bundle) => this.toEntries(bundle))),
    });
  }

  /**
   * The message these bytes came from: its headers and its documents.
   *
   * `null` is the ordinary answer, not a failure: a spreadsheet has no
   * message, and neither has an e-mail imported before the extractor returned
   * one. A refusal is swallowed for the same reason — both the header block
   * and the rail are additions above the document, never a reason to fail the
   * preview of the document itself.
   */
  private bundle(documentId: number): Observable<IPreviewBundle | null> {
    return this.http
      .get<APIResponse<IPreviewBundle>>(`${this.attachmentsUrl}/${documentId}/bundle`)
      .pipe(
        map((response) => response?.object ?? null),
        catchError(() => of(null)),
      );
  }

  /**
   * The rail: the message first, then what it carried, in the file's own order.
   *
   * A document with no `attachment_id` keeps its place with the reason it
   * cannot be opened — an operator looking for a file must never be shown a
   * list that silently lost one.
   */
  private toEntries(bundle: IPreviewBundle | null): IPreviewEntry[] {
    if (!bundle?.body_attachment_id || !bundle.documents?.length) return [];
    const entries: IPreviewEntry[] = [
      {
        key: String(bundle.body_attachment_id),
        label: bundle.headers?.subject || bundle.filename,
        note: null,
        source: () => this.downloadSourceFile(bundle.body_attachment_id as number),
      },
    ];
    for (const document of bundle.documents) {
      entries.push({
        key: document.attachment_id ? String(document.attachment_id) : `skipped:${document.name}`,
        label: document.name,
        // The app's one byte formatter: an attachment size here has to read
        // like an attachment size everywhere else. Empty when the server sent
        // no size, rather than "0 Bytes".
        note: document.size ? this.fileSize.transform(document.size) : null,
        source: document.attachment_id
          ? () => this.downloadSourceFile(document.attachment_id as number)
          : null,
        unavailableReason: document.skipped,
      });
    }
    return entries;
  }

  private downloadSourceFile(documentId: number): Observable<IDownloadedFile> {
    return this.http
      .get(`${this.attachmentsUrl}/${documentId}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((response) => toDownloadedFile(response)));
  }
}
